import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, SidePanel } from 'design-system-web';
import { TranslationKeys } from 'localization';
import { WorkOrderModuleCategoriesSettings } from 'lane-shared/domains/workOrder';
import { useAssignableMembers } from 'lane-shared/domains/workOrder/hooks/useAssignableMembers';
import { TeamCoverageEntry } from './coverageHoursTeamUtils';
import { ALL_ISSUE_TYPES_VALUE } from './coverageHoursPanelUtils';
import { CreateCoverageHours } from './CreateCoverageHours';

const TRANSLATION_KEYS: { [key: string]: TranslationKeys } = Object.freeze({
  title:
    'web.admin.serviceRequest.settings.coverageHours.create.title',
  save:
    'web.admin.serviceRequest.settings.coverageHours.create.save',
  cancel:
    'web.admin.serviceRequest.settings.coverageHours.create.cancel',
  discardChanges:
    'web.admin.serviceRequest.settings.coverageHours.create.discardChanges',
  keepEditing:
    'web.admin.serviceRequest.settings.coverageHours.create.keepEditing',
});

type Props = {
  isOpen: boolean;
  onClose: () => void;
  channelId: string;
  categories: WorkOrderModuleCategoriesSettings[];
};

export function CreateCoverageHoursPanel({
  isOpen,
  onClose,
  channelId,
  categories,
}: Props) {
  const { t } = useTranslation();
  const [, , assignableTeams] = useAssignableMembers(channelId);
  const [categoryValue, setCategoryValue] = useState<string | null>(null);
  const [issueTypeValue, setIssueTypeValue] = useState<string>(
    ALL_ISSUE_TYPES_VALUE
  );
  const [savedTeams, setSavedTeams] = useState<TeamCoverageEntry[]>([]);
  const [isConfirmingClose, setIsConfirmingClose] = useState(false);

  const hasUnsavedChanges = categoryValue !== null || savedTeams.length > 0;

  const handleCategoryChange = (value: string) => {
    setCategoryValue(value);
    setIssueTypeValue(ALL_ISSUE_TYPES_VALUE);
  };

  const handleClose = () => {
    setCategoryValue(null);
    setIssueTypeValue(ALL_ISSUE_TYPES_VALUE);
    setSavedTeams([]);
    setIsConfirmingClose(false);
    onClose();
  };

  const handleCancelClick = () => {
    if (hasUnsavedChanges) {
      setIsConfirmingClose(true);
    } else {
      handleClose();
    }
  };

  return (
    <SidePanel
      id="create-coverage-hours-panel"
      isOpen={isOpen}
      onClose={handleCancelClick}
      header={t(TRANSLATION_KEYS.title)}
      size="medium"
      backdropEnabled
      footerActions={
        isConfirmingClose ? (
          <>
            <Button
              variant="secondary"
              size="large"
              testId="coverage-hours-discard-changes"
              onClick={handleClose}
            >
              {t(TRANSLATION_KEYS.discardChanges)}
            </Button>
            <Button
              variant="primary"
              size="large"
              testId="coverage-hours-keep-editing"
              onClick={() => setIsConfirmingClose(false)}
            >
              {t(TRANSLATION_KEYS.keepEditing)}
            </Button>
          </>
        ) : (
          <>
            <Button variant="primary" size="large" disabled>
              {t(TRANSLATION_KEYS.save)}
            </Button>
            <Button
              variant="secondary"
              size="large"
              testId="coverage-hours-cancel"
              onClick={handleCancelClick}
            >
              {t(TRANSLATION_KEYS.cancel)}
            </Button>
          </>
        )
      }
    >
      <CreateCoverageHours
        categories={categories}
        categoryValue={categoryValue}
        onCategoryChange={handleCategoryChange}
        issueTypeValue={issueTypeValue}
        onIssueTypeChange={setIssueTypeValue}
        assignableTeams={assignableTeams}
        savedTeams={savedTeams}
        onAddTeam={entry => setSavedTeams(prev => [...prev, entry])}
      />
    </SidePanel>
  );
}
